import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ArrowUpRight, Check, Star, ShieldCheck, Zap,
  Database, Cloud, Lock, BarChart3, Receipt, Users, Box,
  Headphones, RefreshCw, BadgeCheck, Infinity,
} from "lucide-react";
import { fadeUp, stagger } from "@/lib/motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

/* ─── PERPETUAL / LIFETIME PLANS ─────────────────────────── */
const PERPETUAL = [
  {
    id: "silver",
    name: "TallyPrime Silver",
    tagline: "Single User • Perpetual License",
    price: "₹22,500",
    gst: "+ 18% GST",
    tssRenew: "TSS Renewal: ₹4,500/yr + GST",
    badge: null,
    color: "#0D47A1",
    icon: ShieldCheck,
    desc: "Perfect for single-user businesses. One-time purchase with lifetime access to the software.",
    features: [
      "1 User license (Single System)",
      "Invoicing & Accounting",
      "Inventory Management",
      "GST & Compliance (e-Invoice, e-Way Bill)",
      "Banking & Cashflow",
      "Connected Services (WhatsApp, Reports)",
      "Business Operations Dashboard",
      "TallyPrime 7.1 with AI features",
    ],
  },
  {
    id: "gold",
    name: "TallyPrime Gold",
    tagline: "Multi User • Perpetual License",
    price: "₹67,500",
    gst: "+ 18% GST",
    tssRenew: "TSS Renewal: ₹13,500/yr + GST",
    badge: "Most Popular",
    color: "#FFA000",
    icon: Star,
    desc: "Unlimited users on LAN — built for growing businesses needing multi-user collaboration.",
    features: [
      "Unlimited Users on LAN",
      "All features of Silver edition",
      "Multi-user concurrent access",
      "GST & Compliance (e-Invoice, e-Way Bill)",
      "Advanced Inventory & Payroll",
      "Banking & Connected Services",
      "Graphical Dashboard & MIS",
      "TallyPrime 7.1 with AI features",
    ],
  },
  {
    id: "server",
    name: "TallyPrime Server",
    tagline: "Enterprise • High Performance",
    price: "₹2,70,000",
    gst: "+ 18% GST",
    tssRenew: "TSS Renewal: Contact us",
    badge: "Enterprise",
    color: "#072A66",
    icon: Database,
    desc: "For large organizations — high-volume transactions, multi-location access and enterprise-grade performance.",
    features: [
      "Unlimited concurrent users",
      "High-volume transaction support",
      "Multi-location & multi-company",
      "On-premise server deployment",
      "Custom integrations & TDL support",
      "Advanced security & audit controls",
      "Dedicated account manager",
      "Enterprise SLA & AMC",
    ],
  },
  {
    id: "auditor",
    name: "TallyPrime Auditor",
    tagline: "Chartered Accountants Edition",
    price: null,
    gst: "+ 18% GST",
    tssRenew: "TSS Renewal: ₹6,750/yr + GST",
    badge: "For CAs",
    color: "#2E7D32",
    icon: BadgeCheck,
    desc: "Designed exclusively for Chartered Accountants to audit and manage multiple clients' books.",
    features: [
      "Audit & analysis tools for CAs",
      "Access multiple clients' data",
      "Comprehensive audit reports",
      "Form 3CA, 3CB, 3CD generation",
      "Verification of books & tallying",
      "GST audit & compliance reports",
      "Advanced data analysis features",
      "Exclusive CA licensing terms",
    ],
  },
  {
    id: "cloud-access",
    name: "TallyPrime Cloud Access",
    tagline: "Access TallyPrime from Anywhere",
    price: "₹600",
    gst: "/user/month + GST",
    tssRenew: "Flexible monthly & annual plans",
    badge: "Cloud",
    color: "#2196F3",
    icon: Cloud,
    desc: "Work with TallyPrime anytime, anywhere. Hosted on Oracle Cloud Infrastructure with dual-layer security and encrypted backups.",
    features: [
      "24x7 secured cloud access",
      "Access via browser or virtual client",
      "Windows, Linux & macOS support",
      "Hosted on Oracle Cloud Infrastructure",
      "Dual-factor authentication",
      "Self Service Portal (SSP)",
      "Encrypted automatic backups",
      "Selective data sharing controls",
    ],
  },
];

/* ─── RENTAL / SUBSCRIPTION PLANS ────────────────────────── */
const RENTAL = [
  {
    id: "rental-silver-1m",
    name: "Silver Rental",
    tagline: "1 Month • Single User",
    price: "₹750",
    gst: "/month + GST",
    color: "#0D47A1",
    icon: Cloud,
    highlight: false,
    features: [
      "1 User (Single System)",
      "All TallyPrime Silver features",
      "Includes updates & upgrades",
      "e-Invoice, e-Way Bill, GST",
      "Auto-renew monthly",
      "Cancel anytime",
    ],
  },
  {
    id: "rental-silver-3m",
    name: "Silver Rental",
    tagline: "3 Months • Single User",
    price: "₹2,138",
    gst: "/3 months + GST",
    color: "#0D47A1",
    icon: Cloud,
    highlight: false,
    features: [
      "1 User (Single System)",
      "All TallyPrime Silver features",
      "Includes updates & upgrades",
      "e-Invoice, e-Way Bill, GST",
      "Pay quarterly, save more",
      "Cancel anytime",
    ],
  },
  {
    id: "rental-gold-1m",
    name: "Gold Rental",
    tagline: "1 Month • Multi User",
    price: "₹2,250",
    gst: "/month + GST",
    color: "#FFA000",
    icon: RefreshCw,
    highlight: true,
    features: [
      "Unlimited Users on LAN",
      "All TallyPrime Gold features",
      "Includes updates & upgrades",
      "e-Invoice, e-Way Bill, GST",
      "Auto-renew monthly",
      "Cancel anytime",
    ],
  },
  {
    id: "rental-gold-3m",
    name: "Gold Rental",
    tagline: "3 Months • Multi User",
    price: "₹6,413",
    gst: "/3 months + GST",
    color: "#FFA000",
    icon: RefreshCw,
    highlight: false,
    features: [
      "Unlimited Users on LAN",
      "All TallyPrime Gold features",
      "Includes updates & upgrades",
      "e-Invoice, e-Way Bill, GST",
      "Pay quarterly, save more",
      "Cancel anytime",
    ],
  },
];

/* ─── TSS RENEWAL PLANS ───────────────────────────────────── */
const TSS = [
  {
    name: "Silver TSS – 1 Year",
    price: "₹4,500",
    gst: "+ 18% GST",
    color: "#0D47A1",
    icon: ShieldCheck,
    features: ["Annual product updates", "e-Invoice & e-Way Bill sync", "Connected banking services", "WhatsApp sharing & reports"],
  },
  {
    name: "Silver TSS – 2 Year",
    price: "₹8,100",
    gst: "+ 18% GST",
    color: "#0D47A1",
    icon: ShieldCheck,
    badge: "Save ~10%",
    features: ["2-year product updates", "e-Invoice & e-Way Bill sync", "Connected banking services", "WhatsApp sharing & reports"],
  },
  {
    name: "Gold TSS – 1 Year",
    price: "₹13,500",
    gst: "+ 18% GST",
    color: "#FFA000",
    icon: Star,
    features: ["Annual product updates", "e-Invoice & e-Way Bill sync", "Multi-user connected services", "WhatsApp sharing & reports"],
  },
  {
    name: "Gold TSS – 2 Year",
    price: "₹24,300",
    gst: "+ 18% GST",
    color: "#FFA000",
    icon: Star,
    badge: "Save ~10%",
    features: ["2-year product updates", "e-Invoice & e-Way Bill sync", "Multi-user connected services", "WhatsApp sharing & reports"],
  },
  {
    name: "Auditor TSS – 1 Year",
    price: "₹6,750",
    gst: "+ 18% GST",
    color: "#2E7D32",
    icon: BadgeCheck,
    features: ["Annual CA edition updates", "Audit & compliance tools", "Multi-client access updates", "GST audit report updates"],
  },
  {
    name: "Auditor TSS – 2 Year",
    price: "₹12,150",
    gst: "+ 18% GST",
    color: "#2E7D32",
    icon: BadgeCheck,
    badge: "Save ~10%",
    features: ["2-year CA edition updates", "Audit & compliance tools", "Multi-client access updates", "GST audit report updates"],
  },
];

const ALL_FEATURES = [
  "Invoicing & Accounting",
  "Inventory Management",
  "GST, e-Invoicing & e-Way Bills",
  "Banking & Cashflow",
  "Connected Services",
  "Business Operations",
  "Payroll & HR",
  "Multi-Currency Support",
  "Graphical Dashboard",
  "WhatsApp Integration",
  "Remote Access",
  "AI-powered features (v7.1)",
];

export default function Products() {
  return (
    <div className="relative bg-[#FAF9F6]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-[#0D47A1] text-white overflow-hidden circuit-pattern">
        <div className="orb bg-[#FFA000] w-[300px] h-[300px] -top-20 -right-20 opacity-15" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-6 transition">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <motion.div variants={stagger(0.1, 0.08)} initial="hidden" animate="show">
            <motion.p variants={fadeUp} className="text-[#FFA000] text-sm font-semibold uppercase tracking-widest mb-3">
              Official Pricing — as on tallysolutions.com
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              TallyPrime <span className="text-[#FFA000]">Products</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="mt-4 text-lg text-white/75 max-w-xl">
              All prices as listed on the official Tally Solutions website. Inclusive of free installation & onboarding by our certified experts.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* What you get in every edition */}
      <section className="py-14 bg-[#0A2E6B] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-center text-sm uppercase tracking-widest text-[#FFA000] font-semibold mb-6">Every TallyPrime edition includes</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {ALL_FEATURES.map((f) => (
              <div key={f} className="flex items-center gap-2 text-sm text-white/80">
                <Check className="w-4 h-4 text-[#FFA000] shrink-0" />
                {f}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Perpetual / Lifetime Plans ── */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div variants={stagger(0.1, 0.08)} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}>
            <motion.span variants={fadeUp} className="eyebrow"><span className="dot" /> Perpetual Plans</motion.span>
            <motion.h2 variants={fadeUp} className="mt-4 font-display font-black text-4xl sm:text-5xl leading-[1.08] tracking-tight text-[#1A1A2E]">
              Buy Once, Own <span className="font-serif-accent text-[#FFA000]">Forever.</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="mt-3 text-[#4A5568] max-w-xl">
              One-time purchase with lifetime access. Renew TSS annually to keep updates & connected services active.
            </motion.p>
          </motion.div>

          <motion.div
            variants={stagger(0.06, 0.08)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {PERPETUAL.map((p) => (
              <motion.div
                key={p.id}
                variants={fadeUp}
                whileHover={{ y: -5 }}
                className={`relative flex flex-col rounded-2xl bg-white border overflow-hidden transition-all duration-500 ${
                  p.badge === "Most Popular"
                    ? "border-[#FFA000] shadow-[0_20px_60px_rgba(255,160,0,0.18)]"
                    : "border-[#0D47A1]/10 shadow-[0_8px_30px_rgba(13,71,161,0.06)] hover:shadow-[0_20px_50px_rgba(13,71,161,0.12)]"
                }`}
              >
                {/* top accent */}
                <div className="h-1.5 w-full" style={{ backgroundColor: p.color }} />

                {p.badge && (
                  <div
                    className="absolute top-4 right-4 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white"
                    style={{ backgroundColor: p.color }}
                  >
                    {p.badge}
                  </div>
                )}

                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${p.color}18` }}>
                      <p.icon className="w-5 h-5" style={{ color: p.color }} />
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-lg text-[#1A1A2E] leading-tight">{p.name}</h3>
                      <span className="text-xs text-[#8896A6]">{p.tagline}</span>
                    </div>
                  </div>

                  <p className="text-[13px] text-[#4A5568] leading-relaxed mb-5">{p.desc}</p>

                  {p.price ? (
                    <div className="flex items-baseline gap-1 mb-1">
                      <span className="font-display font-black text-3xl text-[#1A1A2E]">{p.price}</span>
                      <span className="text-sm text-[#8896A6]">{p.gst}</span>
                    </div>
                  ) : (
                    <div className="mb-1">
                      <span className="font-display font-black text-2xl text-[#1A1A2E]">Contact us</span>
                      <p className="text-[11px] text-[#8896A6]">for exclusive CA pricing</p>
                    </div>
                  )}
                  <p className="text-[11px] text-[#8896A6] mb-5">{p.tssRenew}</p>

                  <ul className="space-y-2 mb-6 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-[13px] text-[#1A1A2E]/80">
                        <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: p.color }} />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/#contact"
                    className="inline-flex items-center justify-center gap-2 rounded-lg text-white px-5 h-10 text-sm font-semibold transition shadow-[0_8px_20px_rgba(13,71,161,0.2)] hover:opacity-90"
                    style={{ backgroundColor: p.color }}
                  >
                    Buy Now <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Rental / Subscription Plans ── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div variants={stagger(0.1, 0.08)} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}>
            <motion.span variants={fadeUp} className="eyebrow"><span className="dot" /> Rental / Subscription</motion.span>
            <motion.h2 variants={fadeUp} className="mt-4 font-display font-black text-4xl sm:text-5xl leading-[1.08] tracking-tight text-[#1A1A2E]">
              Pay Monthly, <span className="font-serif-accent text-[#FFA000]">Cancel Anytime.</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="mt-3 text-[#4A5568] max-w-xl">
              Low upfront cost with all TallyPrime features. Ideal for startups, seasonal businesses and new users.
            </motion.p>
          </motion.div>

          <motion.div
            variants={stagger(0.06, 0.08)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {RENTAL.map((r) => (
              <motion.div
                key={r.id}
                variants={fadeUp}
                whileHover={{ y: -5 }}
                className={`relative flex flex-col rounded-2xl overflow-hidden border transition-all duration-500 ${
                  r.highlight
                    ? "border-[#FFA000] shadow-[0_20px_60px_rgba(255,160,0,0.18)] bg-white"
                    : "border-[#0D47A1]/10 shadow-[0_8px_30px_rgba(13,71,161,0.06)] hover:shadow-[0_20px_50px_rgba(13,71,161,0.1)] bg-white"
                }`}
              >
                <div className="h-1.5 w-full" style={{ backgroundColor: r.color }} />
                {r.highlight && (
                  <div className="absolute top-4 right-4 rounded-full bg-[#FFA000] text-white px-3 py-1 text-[10px] font-bold uppercase tracking-wider">
                    Popular
                  </div>
                )}
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${r.color}18` }}>
                      <r.icon className="w-4 h-4" style={{ color: r.color }} />
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-base text-[#1A1A2E] leading-tight">{r.name}</h3>
                      <span className="text-[11px] text-[#8896A6]">{r.tagline}</span>
                    </div>
                  </div>

                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="font-display font-black text-2xl text-[#1A1A2E]">{r.price}</span>
                    <span className="text-xs text-[#8896A6]">{r.gst}</span>
                  </div>

                  <ul className="space-y-1.5 mb-5 flex-1">
                    {r.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-[12px] text-[#1A1A2E]/80">
                        <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: r.color }} />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/#contact"
                    className="inline-flex items-center justify-center gap-2 rounded-lg text-white px-4 h-9 text-sm font-semibold transition hover:opacity-90"
                    style={{ backgroundColor: r.color }}
                  >
                    Subscribe <ArrowUpRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── TSS Renewal Plans ── */}
      <section className="py-20 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div variants={stagger(0.1, 0.08)} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}>
            <motion.span variants={fadeUp} className="eyebrow"><span className="dot" /> TSS Renewal</motion.span>
            <motion.h2 variants={fadeUp} className="mt-4 font-display font-black text-4xl sm:text-5xl leading-[1.08] tracking-tight text-[#1A1A2E]">
              Keep Your Tally <span className="font-serif-accent text-[#FFA000]">Always Updated.</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="mt-3 text-[#4A5568] max-w-xl">
              Tally Software Services (TSS) ensures you always have the latest version, compliance updates, connected services and support.
            </motion.p>
          </motion.div>

          <motion.div
            variants={stagger(0.06, 0.06)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {TSS.map((t) => (
              <motion.div
                key={t.name}
                variants={fadeUp}
                whileHover={{ y: -4 }}
                className="relative rounded-2xl bg-white border border-[#0D47A1]/10 p-5 shadow-[0_8px_30px_rgba(13,71,161,0.06)] hover:shadow-[0_20px_50px_rgba(13,71,161,0.1)] transition-all duration-400"
              >
                {t.badge && (
                  <div className="absolute top-4 right-4 rounded-full bg-green-100 text-green-700 px-3 py-1 text-[10px] font-bold uppercase tracking-wider">
                    {t.badge}
                  </div>
                )}
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${t.color}18` }}>
                    <t.icon className="w-4 h-4" style={{ color: t.color }} />
                  </div>
                  <h3 className="font-display font-bold text-base text-[#1A1A2E]">{t.name}</h3>
                </div>
                <div className="flex items-baseline gap-1 mb-3">
                  <span className="font-display font-black text-2xl text-[#1A1A2E]">{t.price}</span>
                  <span className="text-xs text-[#8896A6]">{t.gst}</span>
                </div>
                <ul className="space-y-1.5 mb-4">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-[12px] text-[#1A1A2E]/80">
                      <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: t.color }} />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/#contact"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold transition hover:underline"
                  style={{ color: t.color }}
                >
                  Renew Now <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0D47A1] text-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <motion.div variants={stagger(0.1, 0.08)} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.5 }}>
            <motion.h2 variants={fadeUp} className="font-display font-black text-3xl sm:text-4xl mb-4">
              Not sure which plan is right for you?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-white/75 mb-8">
              Our certified Tally experts will help you choose the best edition for your team size and business needs — at no charge.
            </motion.p>
            <motion.div variants={fadeUp}>
              <Link
                to="/#contact"
                className="inline-flex items-center gap-2 rounded-lg bg-[#FFA000] hover:bg-[#E69000] text-white px-8 h-12 text-[15px] font-bold transition shadow-[0_15px_40px_rgba(255,160,0,0.4)]"
              >
                Get Free Consultation <ArrowUpRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
